import React from "react";
import { observer } from "mobx-react";
import AllIcon from "../../../../../images/sidebar/selected-detail-icon.svg";
import TrioBallsIcon from "../../../../../images/common/ball-trio.svg";
import ServiceIcon from "../../../../../images/common/service.svg";
import ProductIcon from "../../../../../images/common/product.svg";
import { RelatedItemsCategoriesModel, SelectItemsPopupStore } from "../models";
import { SelectItemTypes } from "../enums";
import {
  BenefitModalWrapper,
  CustomLabel,
  FlexContainer,
  Input,
  ITableHeaders,
  Loader,
  ModalComponent,
  RedButton,
  SapphireBlueButton,
  SingleSelect,
  ToastService,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import { AuthService, ItemService } from "../services";
import SelectedItemContent from "./SelectedItemContent"; 
import TableGrid from "./TableGrid";

type Props = {
  onClose: () => void;
  onAddClick: (items: RelatedItemsCategoriesModel) => void;
  multiplePriceSelection: boolean;
  sourceId?: string;
  exceptItems?: RelatedItemsCategoriesModel[];
};

type OptionsData = {
  icon: string;
  label: string;
  value: string;
};

const options: OptionsData[] = Object.keys(SelectItemTypes).map((key) => {
  const icon = getIcon(key);
  return {
    icon: icon,
    label: SelectItemTypes[key as keyof typeof SelectItemTypes],
    value: key,
  };
});

function getIcon(key: string) {
  let icon = "";
  switch (key) {
    case "All":
      icon = AllIcon;
      break;
    case "Service":
      icon = ServiceIcon;
      break;
    case "Product":
      icon = ProductIcon;
      break;
    case "Membership":
      icon = TrioBallsIcon;
      break;
  }
  return icon;
}

@observer
class SelectItemsPopup extends React.Component<Props> {
  public itemsPopupStore = new SelectItemsPopupStore();
  private loader: Loader = new Loader(false, "", "255px");

  componentDidMount(): void {
    this.loadItems();
  }

  loadItems = () => {
    const { typeFilter, searchValue } = this.itemsPopupStore;
    this.loader.show();
    ItemService.getAvailableTypes(
      AuthService.authData.accountId,
      1,
      9999,
      typeFilter,
      searchValue,
      false,
      this.props.sourceId
    ).subscribe({
      next: (response) => {
        this.loader.hide();
        if (!response.result) {
          ToastService.showError("Error in loading items");
          return;
        }
        this.itemsPopupStore.deserializeAPIModel(response.result);
      },
    });
  };

  onSearch(value: string) {
    this.itemsPopupStore.setSearchValue(value);
    this.loadItems();
  }

  onChangeActiveTab(value: string) {
    this.itemsPopupStore.setActiveTab(value);
  }

  onSelectItem(value: RelatedItemsCategoriesModel) {
    this.onChangeActiveTab("selectedItem");
    if (value.id !== this.itemsPopupStore.selectedItem.id) {
      const defaultPrice =
        value.prices.find((x) => x.isDefaultPrice) || value.prices[0];

      this.itemsPopupStore.updateSelectedItem(
        new RelatedItemsCategoriesModel({
          ...value,
          selectedPriceIds: defaultPrice ? [defaultPrice.id] : [],
          selectedVariantIds: [],
          quantity: "1",
          canChooseAvailableVariants: value.isProductItem() ? true : false,
        })
      );
    } else {
      this.itemsPopupStore.updateSelectedItem(value);
    }
  }

  onChangeFilter(value: string) {
    this.itemsPopupStore.setTypeFilter(value);
    this.loadItems();
  }

  onSelectPrice(priceId: string, checked: boolean) {
    const updatedSelectedItem = new RelatedItemsCategoriesModel(
      this.itemsPopupStore.selectedItem
    );
    if (checked) {
      if (updatedSelectedItem.selectedPriceIds.indexOf(priceId) === -1) {
        if (this.props.multiplePriceSelection) {
          updatedSelectedItem.selectedPriceIds.push(priceId);
        } else {
          //FOR SINGLE SELECTION JUST ADD ONE
          updatedSelectedItem.selectedPriceIds = [];
          updatedSelectedItem.selectedPriceIds = [priceId];
        }
      }
    } else {
      updatedSelectedItem.selectedPriceIds =
        updatedSelectedItem.selectedPriceIds.filter((x) => x !== priceId);
    }
    this.itemsPopupStore.updateSelectedItem(updatedSelectedItem);
  }

  onSelectVariant(variantId: string, checked: boolean) {
    const updatedSelectedItem = new RelatedItemsCategoriesModel(
      this.itemsPopupStore.selectedItem
    );
    if (checked) {
      if (updatedSelectedItem.selectedVariantIds.indexOf(variantId) === -1) {
        updatedSelectedItem.selectedVariantIds.push(variantId);
      }
    } else {
      updatedSelectedItem.selectedVariantIds =
        updatedSelectedItem.selectedVariantIds.filter((x) => x !== variantId);
    }
    this.itemsPopupStore.updateSelectedItem(updatedSelectedItem);
  }

  // get getItemGroups() {
  //   const chunkSize = 6;
  //   const array: any = [];

  //   let clonedItems = this.itemsPopupStore.items.map((x) => {
  //     return new RelatedItemsCategoriesModel(x);
  //   });

  //   if (this.itemsPopupStore.inStockOnly) {
  //     switch (this.itemsPopupStore.typeFilter) {
  //       case "All":
  //         clonedItems = clonedItems.filter(
  //           (x) => x.type === "Product" && x.inStockOnly
  //         );
  //         break;
  //       case "Product":
  //         clonedItems = clonedItems.filter(
  //           (x) => x.type === "Product" && x.inStockOnly
  //         );
  //     }
  //   }

  //   for (let i = 0; i < clonedItems.length; i += chunkSize) {
  //     const chunk = clonedItems.slice(i, i + chunkSize);
  //     array.push(chunk);
  //   }
  //   return array;
  // }

  get selectItemsColumns() {
    const columns: ITableHeaders<RelatedItemsCategoriesModel>[] = [
      {
        dataKey: "type",
        title: "TYPE",
        onCellRender: (data: RelatedItemsCategoriesModel) => {
          return <img width="20px" height="20px" src={getIcon(data.type)} />;
        },
        cellClassName: "table-col-5 text-center",
        className: "table-col-5 text-center",
      },
      {
        dataKey: "name",
        title: "ITEM NAME",
        onCellRender: (data: RelatedItemsCategoriesModel) => {
          return data.name;
        },
        cellClassName: "table-col-15",
        className: "table-col-15",
      },
      {
        dataKey: "quantity",
        title: "OPTIONS",
        onCellRender: (data: RelatedItemsCategoriesModel) => {
          return data.type === "Product"
            ? data.hasVariants
              ? `${data.variants.options.length} Variants`
              : ""
            : data.prices[0].terms.termIntervalCount > 0
              ? `Length ${data.prices[0].terms.termIntervalCount} ${data.prices[0].terms.termInterval}`
              : "";
        },
        cellClassName: "table-col-10",
        className: "table-col-10",
      },
      {
        dataKey: "priceDisplay",
        title: "PRICE",
        onCellRender: (data: RelatedItemsCategoriesModel) => {
          return data.priceDisplay;
        },
        cellClassName: "table-col-right table-col-10",
        className: "table-col-right table-col-10",
      },
      {
        dataKey: "id",
        title: "",
        onCellRender: (data: RelatedItemsCategoriesModel) => {
          return (
            <SapphireBlueButton
              primary
              className="add-item-btn"
              onClick={() => this.onSelectItem(data)}
            >
              Add
            </SapphireBlueButton>
          );
        },
        cellClassName: "table-col-5",
        className: "table-col-5",
      },
    ];
    return columns;
  }

  get clonedItems() {
    const clonedItems = this.itemsPopupStore.items.map((x) => {
      return new RelatedItemsCategoriesModel(x);
    });

    if (this.props.exceptItems && this.props.exceptItems.length > 0) {
      return clonedItems.filter(
        (x) =>
          this.props.exceptItems?.findIndex(
            (y) => y.id === x.id && y.type === x.type
          ) === -1
      );
    }
    return clonedItems;
  }

  get itemsContent() {
    const gridItems = this.itemsPopupStore.inStockOnly
      ? this.clonedItems.filter((x) => x.isItemUnavailable !== true)
      : this.clonedItems;
    return (
      <>
        <FlexContainer spacing={20} alignItems="center">
          <div>
            <FlexContainer
              spacing={15}
              fxLayout="row"
              className="search-input-wrapper"
            >
              <div>
                <Input
                  value={this.itemsPopupStore.searchValue}
                  type="text"
                  onChange={(e) => this.onSearch(e.target.value)}
                  className="styled-input search-input"
                />
              </div>
            </FlexContainer>
          </div>
          <div>
            <SingleSelect
              selectedKey={this.itemsPopupStore.typeFilter}
              nameKeys={["label"]}
              valueKeys={["value"]}
              options={[options]}
              customHeader={
                <FlexContainer
                  spacing={10}
                  alignItems="center"
                  padding="0px 10px"
                >
                  <img
                    width="20px"
                    height="20px"
                    src={getIcon(this.itemsPopupStore.typeFilter)}
                  />
                  {
                    SelectItemTypes[
                      this.itemsPopupStore
                        .typeFilter as keyof typeof SelectItemTypes
                    ]
                  }
                </FlexContainer>
              }
              customOption={(option: OptionsData) => (
                <FlexContainer
                  padding="5px"
                  key={option.label}
                  spacing={10}
                  alignItems="center"
                  onClick={() => this.onChangeFilter(option.value)}
                >
                  <img width="20px" height="20px" src={option.icon} />
                  <span>{option.label}</span>
                </FlexContainer>
              )}
              onChange={(value) => this.onChangeFilter(value.value)}
              className={"dpdn-with-icons"}
              searchable={false}
            />
          </div>
          <div>
            <Input
              type="checkbox"
              value={this.itemsPopupStore.inStockOnly}
              onChange={() => this.itemsPopupStore.toggleInStockOnly()}
              label={"In Stock Only"}
            />
          </div>
        </FlexContainer>
        <CustomLabel margin="30px 0px 20px">
          <span className="semi-bold">Popular Items</span> (hover to select)
        </CustomLabel>
        {this.loader.showLoader ? (
          this.loader.loaderComponent
        ) : (
          <TableGrid
            totalItemCount={gridItems.length}
            cellSpacing={20}
            data={gridItems}
            headers={this.selectItemsColumns}
            height={"calc(100vh - 499px)"}
            className="select-items-table"
          />
        )}
      </>
    );
  }

  get selectedItemContent() {
    const { selectedItem } = this.itemsPopupStore;
    if (!selectedItem) return null;
    return (
      <>
        <SelectedItemContent
          toggleCanChooseAvailableVariant={() =>
            this.itemsPopupStore.updateSelectedItem(
              new RelatedItemsCategoriesModel({
                ...new RelatedItemsCategoriesModel(
                  this.itemsPopupStore.selectedItem
                ),
                canChooseAvailableVariants:
                  !this.itemsPopupStore.selectedItem.canChooseAvailableVariants,
              })
            )
          }
          selectedItem={selectedItem}
          onSelectPrice={(id: string, checked: boolean) =>
            this.onSelectPrice(id, checked)
          }
          onSelectVariant={(id: string, checked: boolean) =>
            this.onSelectVariant(id, checked)
          }
          onUpdate={(value: RelatedItemsCategoriesModel) =>
            this.itemsPopupStore.updateSelectedItem(value)
          }
          multiplePriceSelection={this.props.multiplePriceSelection}
        />
      </>
    );
  }

  get modalBody(): React.ReactNode {
    const { activeTab } = this.itemsPopupStore;
    switch (activeTab) {
      case "items":
        return this.itemsContent;
      case "selectedItem":
        return this.selectedItemContent;
    }
  }

  validateInputs() {
    const { selectedItem } = this.itemsPopupStore;
    if (selectedItem.hasVariants && !selectedItem.canChooseAvailableVariants) {
      if (
        !selectedItem.selectedVariantIds ||
        selectedItem.selectedVariantIds.length == 0
      ) {
        return false;
      }
    } else if (selectedItem.selectedPriceIds.length == 0) {
      return false;
    }

    if (!UtilityMethods.validateDecimalValue(selectedItem.quantity, true)) {
      return false;
    }
    return true;
  }

  onAddItems() {
    if (!this.validateInputs()) {
      return;
    }

    this.props.onAddClick(this.itemsPopupStore.selectedItem);
  }
  render() {
    const { activeTab, selectedItem } = this.itemsPopupStore;
    return (
      <BenefitModalWrapper className="add-item-popup">
        <ModalComponent
          title="Add Item"
          headerSubBar={
            activeTab === "selectedItem" ? selectedItem?.name || "" : ""
          }
          titleFontSize="22px"
          // maxHeight="400px"
          bodyPadding="40px 50px"
          onClose={() => this.props.onClose()}
          showFooter={true}
          wrapperWidth="800px"
          bodyBackgroundColor="#fff"
          footerHeight="auto"
          scrollColor="rgba(21, 40, 55, 0.3)"
          // bodyHeight="500px"       // removed this because it was causing the modal to be cut off
          bodyClass={`selected-item-popup ${activeTab === "selectedItem" ? "selected-item" : ""}`}
          footerContent={
            <FlexContainer
              alignItems="center"
              justifyContent="center"
              spacing={15}
            >
              {activeTab !== "items" && (
                <SapphireBlueButton
                  minWidth="120px"
                  height="40px"
                  onClick={() => this.onChangeActiveTab("items")}
                >
                  Back
                </SapphireBlueButton>
              )}
              <RedButton
                minWidth="120px"
                height="40px"
                onClick={() => this.props.onClose()}
              >
                Cancel
              </RedButton>
              <SapphireBlueButton
                primary
                minWidth="120px"
                height="40px"
                disabled={this.validateInputs() ? false : true}
                onClick={() => this.onAddItems()}
              >
                Add Item
              </SapphireBlueButton>
            </FlexContainer>
          }
        >
          {this.modalBody}
        </ModalComponent>
      </BenefitModalWrapper>
    );
  }
}

export default SelectItemsPopup;
